import { useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { ChevronDown, ExternalLink } from "lucide-react";
import { SmartLink } from "@/components/SmartLink";
import { useNavItems } from "@/components/layout/Header";
import { useSite } from "@/context/SiteContext";

const slug = (s) => s.toLocaleLowerCase("tr").normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/ı/g, "i").replace(/[^a-z0-9]+/g, "-");
const linkCls = ({ isActive }) =>
  `relative px-3 py-2 text-[15px] font-semibold transition-colors duration-150 hover:text-navy ${isActive ? "text-navy after:absolute after:inset-x-3 after:-bottom-[1px] after:h-[3px] after:bg-brand-red" : "text-ink-soft"}`;

const Dropdown = ({ item }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const { pathname } = useLocation();
  const active = item.children.some((c) => pathname.startsWith(c.to)) || (item.to && pathname.startsWith(item.to));
  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    const close = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  return (
    <div ref={ref} className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}
      onKeyDown={(e) => e.key === "Escape" && setOpen(false)}>
      <button type="button" aria-expanded={open} aria-haspopup="true" onClick={() => setOpen((v) => !v)}
        className={`${linkCls({ isActive: active })} inline-flex items-center gap-1`} data-testid={`header-dropdown-${slug(item.label)}`}>
        {item.label}
        <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${open ? "rotate-180" : ""}`} aria-hidden="true" />
      </button>
      {open && (
        <div className="absolute left-0 top-full z-50 min-w-[240px] pt-2">
          <ul className="animate-fade-up rounded-md border border-line bg-white p-2 shadow-lg" data-testid={`header-dropdown-menu-${slug(item.label)}`}>
            {item.children.map((c) => (
              <li key={c.to}>
                <NavLink to={c.to} className={({ isActive }) => `block rounded px-3 py-2 text-[15px] transition-colors hover:bg-navy-50 hover:text-navy ${isActive ? "bg-navy-50 font-semibold text-navy" : "text-ink-soft"}`}
                  data-testid={`header-nav-${slug(c.label)}`}>
                  {c.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export const DesktopNav = () => {
  const items = useNavItems();
  const site = useSite();
  return (
    <nav aria-label="Ana menü" className="hidden items-center gap-1 xl:flex" data-testid="desktop-nav">
      {items.map((item) => {
        if (item.children?.length) return <Dropdown key={item.label} item={item} />;
        if (item.external)
          return (
            <SmartLink key={item.label} item={item} testId="header-mkk-link" title={site.mkk_note || undefined}
              className="ml-2 inline-flex items-center gap-1.5 rounded-md border border-navy px-3.5 py-2 text-[15px] font-semibold text-navy transition-colors duration-150 hover:bg-navy hover:text-white">
              {item.label} <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
            </SmartLink>
          );
        return (
          <NavLink key={item.label} to={item.to} end={item.to === "/"} className={linkCls} data-testid={`header-nav-${slug(item.label)}`}>
            {item.label}
          </NavLink>
        );
      })}
    </nav>
  );
};
